/*
 * $Id:: Header.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useEffect } from 'react';

import { IconButton } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import Link from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import MenuIcon from '@material-ui/icons/Menu';
import MenuOpen from '@material-ui/icons/MenuOpen';
import { useDispatch, useSelector } from 'react-redux';

import { homepage } from '../../../package.json';
import { globals as globalsSlice } from '../../app/globalsSlice';
import LogoIcon from '../../assets/images/logoinpi.svg';
import LogoIconEn from '../../assets/images/logoinpi_en.svg';
import { me as meSlice } from '../../pages/AuthenticatedPage/authenticationSlice';
import { SLICE_NAME } from '../../pages/AuthenticatedPage/constants';
import { details as detailsSlice } from '../../pages/MyAccountPage/accountSlice';
import helpers from '../../pages/SSOPage/SSOHelpers';
import { t } from '../../providers';
import ConfirmLogoutDialog from './ConfirmLogoutDialog';
import DrawerSidebar from './DrawerSidebar';
import UserInfo from './UserInfo';
import UserinfoMobile from './UserInfoMobile';
import ImpersonationBanner from '../ImpersonationBanner';

const useStyles = makeStyles((theme) => ({
    headerWrapper: {
        backgroundColor: '#ffffff',
        boxShadow: '0px 3px 3px rgba(0, 0, 0, 0.1)',
        height: '85px',
    },
    header: {
        color: theme.palette.common.white,
        padding: '0px',
        width: '100%',
        display: 'flex',
        flexFlow: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        '@media (max-width:960px)': {
            padding: '0 20px',
        },
    },
    headerToolbar: {
        minHeight: '85px',
        paddingLeft: '26px',
        paddingRight: '20px',
        '@media (max-width: 960px)': {
            paddingLeft: 'unset',
            paddingRight: 'unset',
        },
    },
    logo: {
        cursor: 'pointer',
        padding: '4px',
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
    },

    leftSection: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
    },
    menuButton: {
        marginRight: '10px',
        borderRadius: '0',
        '& svg': {
            fill: 'black',
            opacity: '.54',
            width: '24px !important',
            '&:hover': {
                opacity: '.35',
            },
        },
        '&.MuiButtonBase-root': {
            margin: '0 20px 0 0',
        },
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
    },
}));

const Header = ({ smallDevice, setShowMenu, showMenu }) => {
    const classes = useStyles();
    const me = useSelector(meSlice);
    const dispatch = useDispatch();
    const details = useSelector(detailsSlice);
    const { globals } = useSelector(globalsSlice);
    const languages = Object.keys(globals.supportedLanguages || {}).map((key) => globals.supportedLanguages[key]);
    let myStorage = window.sessionStorage;
    let userLang = myStorage.getItem('lang');
    const lang = userLang ? userLang : me && me.language ? me.language : 'pt';
    const [language, setLanguage] = useState(lang);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [user, setUser] = useState({
        fullName: (me || {}).fullName,
        role: (me || {}).personType,
        userType: (me || {}).userType,
        authorities: (me || {}).authorities,
    });
    const [showImpersonationBanner, setShowImpersonationBanner] = useState(false);

    useEffect(() => {
        const user = me || {};
        setLanguage(userLang ? userLang : user.language ? me.language : 'pt');
        setUser({
            fullName: !user.childFullName ? user.fullName || `${user.firstName} ${user.surName}` : user.fullName,
            userRole: user.personType || '',
            userType: user.userType || '',
            userDetails: details || {},
            authorities: user.authorities,
        });
        // TODO: add condition for impersonation
        setShowImpersonationBanner((me && me.impersonate) || false);
    }, [me, details]);

    const isLoggedIn = !!(me || {}).userName;

    const logout = (_event) => {
        setDialogOpen(false);
        let myStorage = window.sessionStorage;
        let userLang = myStorage.getItem('lang');
        if (userLang) {
            myStorage.removeItem('lang');
        }
        helpers.logout();
    };

    const navigateToHomepage = () => {
        dispatch({ type: `${SLICE_NAME}/error`, payload: null });
    };

    const initiateLogout = () => {
        setDialogOpen(true);
    };

    const cancelLogout = (event) => {
        event.preventDefault();
        setDialogOpen(false);
    };

    return (
        <AppBar className={classes.headerWrapper} data-testid="HeaderTestId">
            <noscript>You need to enable JavaScript to run this app.</noscript>
            <Toolbar className={classes.headerToolbar}>
                <div className={classes.header}>
                    <div className={classes.leftSection}>
                        {smallDevice && <DrawerSidebar />}
                        {!smallDevice && (
                            <IconButton
                                aria-controls="sidebar-menu-container"
                                aria-expanded={showMenu}
                                aria-haspopup={true}
                                color="inherit"
                                aria-label={showMenu ? t('close.main.navigation') : t('open.main.navigation')}
                                onClick={() => setShowMenu(!showMenu)}
                                edge="start"
                                className={classes.menuButton}
                                id="burgerMenuBtn">
                                {showMenu ? <MenuOpen aria-hidden="true" focusable="false" /> : <MenuIcon aria-hidden="true" focusable="false" />}
                            </IconButton>
                        )}
                        <Link aria-label={t('go.to.homepage')} href={`${homepage}`} onClick={navigateToHomepage} id="logo" className={classes.logo}>
                            <img src={!lang || lang === 'pt' ? LogoIcon : LogoIconEn} alt={t('header.logo')} style={{ width: 206 }} />
                        </Link>
                    </div>
                    {isLoggedIn && (
                        <UserInfo
                            smallDevice={smallDevice}
                            user={user}
                            languages={languages}
                            language={language}
                            logout={initiateLogout}
                            myAccountLink={`${globals.accountLinks['inpi.account.text']['link']}${language}`}
                        />
                    )}
                </div>
            </Toolbar>
            {showImpersonationBanner && (
                <div style={{ width: '100%' }}>
                    <ImpersonationBanner user={user} />
                </div>
            )}
            {dialogOpen && <ConfirmLogoutDialog open={dialogOpen} onAccept={logout} onCancel={cancelLogout} />}
        </AppBar>
    );
};

export default Header;
